import React, { useEffect } from 'react';
import {
	Grid,
	Container,
	TextField,
	FormControl,
	FormHelperText,
	InputLabel,
	Select,
	MenuItem,
	Box
} from '@material-ui/core';
import clsx  from 'clsx';
import PropTypes from 'prop-types';
import PaymentInfoV3 from '../PaymentInfoV3/PaymentInfoV3';
import { withNamespaces } from 'react-i18next';
import utils from '../../../services/utils';
import { PlanTypes } from '../../../config/plan-types';

import { withStyles } from '@material-ui/core/styles';
import { useStyles } from './FormsV3Styles';

const { REACT_APP_IDEAL } = process.env;

function IDealFormV3({
	t,
	mbg,
	isApplicableInTheCountry,
	upgradeMode,
	planConfig,
	savingPercent,
	saving2Percent,
	classes,
	saving,
	currency,
	selectedPackage,
	formFields,
	paymentFields,
	withPassword,
	fieldsError,
	fieldsTouched,
	btnDisabled,
	btnProcessing,
	couponCode,
	couponPrice,
	couponGracePeriod,
	couponValidPlan,
	couponSavingPercent,
	couponRenewalPrice,
	couponMonthly,
	couponYearly,
	couponYearly2,
	isProxyDetected,
	iDealBankList,
	onChangePaymentMethod,
	onChangeCouponCodeField,
	onChangeFormFields,
	onBlur,
	onInit,
	onSubmit,
	selectedPaymentMethod
}) {

	useEffect(() => {
		if(!paymentFields[selectedPaymentMethod] && selectedPaymentMethod === REACT_APP_IDEAL) {
			onInit({
				[selectedPaymentMethod] : {
					firstName: '',
					lastName: '',
					bic: ''
				}
			});
		}
	}, [onInit, paymentFields, selectedPaymentMethod]);

	const _pf = paymentFields[selectedPaymentMethod] || {};
	const _pft = fieldsTouched[selectedPaymentMethod] || {};
	const _pfe = fieldsError[selectedPaymentMethod] || {};
	const _ff = {...formFields} || {};
	const _ft = {...fieldsTouched['formFields']} || {};
	const _fe = {...fieldsError['formFields']} || {};

	if(!withPassword) {
		delete _ff.password;
		delete _ft.password;
		delete _fe.password;
	}

	const _couponByPlan = utils.executeCouponByPlan(selectedPackage, PlanTypes, couponYearly, couponYearly2, couponMonthly);
	const _paymentNotEligible = _couponByPlan.code && utils.checkPaymentEligibility(_couponByPlan.payment_methods, REACT_APP_IDEAL);
	const disabled = (Object.keys(_pfe).length + Object.keys(_fe).filter(i => i !== 'termsAndConditions').length > 0) || (Object.keys(_ff).filter(i => i !== 'termsAndConditions').length + Object.keys(_pf).length !== Object.keys(_pft).length + Object.keys(_ft).filter(i => i !== 'termsAndConditions').length) || btnDisabled || btnProcessing || _paymentNotEligible;

	return (
		<Container component="main">
			<Box pb={2}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<TextField
							variant="outlined"
							fullWidth
							label={t('First Name')}
							placeholder={t('Enter first name')}
							InputLabelProps={{
								shrink: true,
							}}
							name="firstName"
							classes={{root: classes.fieldContainer}}
							value={_pf.firstName || ''}
							error={!!_pfe.firstName}
							helperText={(_pfe.firstName) ? <span className={classes.errorIcon}>{_pfe.firstName}</span> : ''}
							onChange={e => onChangeFormFields(e.target.value, 'firstName')}
							onBlur={() => onBlur('firstName', selectedPaymentMethod)}
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<TextField
							variant="outlined"
							fullWidth
							label={t('Last Name')}
							placeholder={t('Enter last name')}
							InputLabelProps={{
								shrink: true,
							}}
							name="lastName"
							classes={{root: classes.fieldContainer}}
							value={_pf.lastName || ''}
							error={!!_pfe.lastName}
							helperText={(_pfe.lastName) ? <span className={classes.errorIcon}>{_pfe.lastName}</span> : ''}
							onChange={e => onChangeFormFields(e.target.value, 'lastName')}
							onBlur={() => onBlur('lastName', selectedPaymentMethod)}
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<FormControl variant="outlined" classes={{root: classes.fieldContainer}} fullWidth error={!!_pfe.bic}>
							<InputLabel shrink>{t('Select BIC')}</InputLabel>
							<Select
								value={_pf.bic || ''}
								onChange={e => onChangeFormFields(e.target.value, 'bic')}
								onClose={() => onBlur('bic', selectedPaymentMethod)}
								label={t('Select BIC')}
								displayEmpty
								renderValue={
									_pf.bic !== '' ? undefined : () => <div className={classes.placeholder}>{t('Select BIC')}</div>
								}
							>
								{iDealBankList.map(c => (
									<MenuItem
										key={c.bic}
										className={clsx(classes.countrySelectItem, {[classes.countrySelectItemActive]: c.value === formFields.country})}
										value={c.bic}>{c.name}</MenuItem>
								))}
							</Select>

							<FormHelperText>{(_pfe.bic) ? <span className={classes.errorIcon}>{_pfe.bic}</span> : ''}</FormHelperText>
						</FormControl>
					</Grid>

					<Grid item xs={12} style={{display: (isApplicableInTheCountry ? 'none' : 'block')}}>
						<div className={classes.countryWarning}>
							{t('Based on your current location, the selected payment method may not be available.')}
						</div>
					</Grid>

					<Grid item xs={12}>
						<PaymentInfoV3
							_pf={_pf}
							_pft={_pft}
							_pfe={_pfe}
							paymentNotEligible={_paymentNotEligible}
							paymentDisplayName="iDeal"
							saving={saving}
							savingPercent={savingPercent}
							saving2Percent={saving2Percent}
							selectedPackage={selectedPackage}
							currency={currency}
							couponByPlan={_couponByPlan}
							couponCode={couponCode}
							couponPrice={couponPrice}
							couponGracePeriod={couponGracePeriod}
							couponValidPlan={couponValidPlan}
							couponSavingPercent={couponSavingPercent}
							couponRenewalPrice={couponRenewalPrice}
							upgradeMode={upgradeMode}
							planConfig={planConfig}
							disabled={disabled}
							isProxyDetected={isProxyDetected}
							mbg={mbg}
							info={true}
							btnProcessing={btnProcessing}
							onChangePaymentMethod={onChangePaymentMethod}
							onChangeCouponCodeField={onChangeCouponCodeField}
							onChangeFormFields={onChangeFormFields}
							onSubmit={onSubmit}
						/>
					</Grid>
				</Grid>
			</Box>
		</Container>
	);
}

IDealFormV3.propTypes = {
	i18n: PropTypes.object,
	mbg: PropTypes.bool,
	isApplicableInTheCountry: PropTypes.bool,
	upgradeMode: PropTypes.bool,
	planConfig: PropTypes.object,
	classes: PropTypes.object,
	currency: PropTypes.string,
	savingPercent: PropTypes.string,
	saving2Percent: PropTypes.string,
	saving: PropTypes.object,
	selectedPackage: PropTypes.object,
	formFields: PropTypes.object,
	paymentFields: PropTypes.object,
	withPassword: PropTypes.bool,
	fieldsError: PropTypes.object,
	fieldsTouched: PropTypes.object,
	btnDisabled: PropTypes.bool,
	btnProcessing: PropTypes.bool,
	selectedPaymentMethod: PropTypes.string,
	couponYearly: PropTypes.object,
	couponCode: PropTypes.string,
	couponPrice: PropTypes.string,
	couponGracePeriod: PropTypes.number,
	couponValidPlan: PropTypes.string,
	couponSavingPercent: PropTypes.string,
	couponRenewalPrice: PropTypes.number,
	couponMonthly: PropTypes.object,
	couponYearly2: PropTypes.object,
	isProxyDetected: PropTypes.bool,
	iDealBankList: PropTypes.array,
	onChangePaymentMethod: PropTypes.func,
	onChangeCouponCodeField: PropTypes.func,
	onChangeFormFields: PropTypes.func,
	onBlur: PropTypes.func,
	onInit: PropTypes.func,
	onSubmit: PropTypes.func,
	t: PropTypes.func
};

export default withStyles(useStyles)(withNamespaces()(IDealFormV3));